// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — StartNode: Rounded rect, navy fill
// 100% sizing pattern (same as ActivityNode)
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { StartNodeData, FlowNodeData } from '../../types/flowchart.types';

const W = T.node.width;
const H = T.node.height;

function StartNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as StartNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label || 'Inicio');
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label || 'Inicio');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={60}
        minHeight={30}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox={`0 0 ${W} ${H}`}>
        <rect
          x={T.shape.strokeWidthNavy / 2}
          y={T.shape.strokeWidthNavy / 2}
          width={W - T.shape.strokeWidthNavy}
          height={H - T.shape.strokeWidthNavy}
          rx={T.shape.borderRadiusStart}
          ry={T.shape.borderRadiusStart}
          fill={T.color.navy}
          stroke={T.color.navyDark}
          strokeWidth={T.shape.strokeWidthNavy}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
          boxShadow: selected ? `0 0 0 2px ${T.color.nodeStrokeSelected}` : undefined,
          borderRadius: T.shape.borderRadiusStart,
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.navyText,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.navyText,
              textAlign: 'center',
            }}
          >
            {d.label || 'Inicio'}
          </span>
        )}
      </div>
    </div>
  );
}

export const StartNode = memo(StartNodeComponent);
